import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getAssessmentPeriods,
  getMostRecentAssessmentPeriod,
  getSelectedAssessmentPeriod,
  getSelectedAssessmentPeriodCategories,
} from 'common/selectors/auth';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';
import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import Select from 'react-select';
import CategorySelect from 'common/components/CategorySelect';
import { PageContent, PageHeader, PageSection } from '../../../common/styles/page';
import DashboardCard from 'areas/dashboard/components/DashboardCard';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { StyledIconButton } from 'common/components/button/StyledButton';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import styled from 'styled-components';
import { hexToRgb } from 'common/styles/colors';

import { AggregateType } from 'common/constants/AggregateType';
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

type SelectOption = { value: string; label: string };

const OutcomesQuestions = ({ assessmentPeriodId }: { assessmentPeriodId: number | null }) => {
  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod) as any;
  const mostRecentAssessmentPeriod = useSelector(getMostRecentAssessmentPeriod) as any;
  const assessmentPeriods = useSelector(getAssessmentPeriods) as any[];
  const selectedCategories = useSelector(getSelectedAssessmentPeriodCategories) as any[];

  const {
    data: areaOutcomesData,
    currentData: currentAreaOutcomesData,
    isLoading: isLoadingAreaOutcomes,
    isFetching: isFetchingAreaOutcomes,
  } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.OPTION_OUTCOME_SCORES,
    assessmentPeriodId: assessmentPeriodId ?? undefined,
  });

  const [selectedOutcome, setSelectedOutcome] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro } = usePageCopilotPrompts();

  const contextAssessmentPeriod =
    (Array.isArray(assessmentPeriods)
      ? assessmentPeriods.find((period: any) => period?.id?.toString?.() === assessmentPeriodId?.toString?.())
      : undefined) ||
    selectedAssessmentPeriod ||
    mostRecentAssessmentPeriod;

  const selectedOutcomeOptions = contextAssessmentPeriod?.outcomes || [];
  const activeAssessmentPeriodId =
    contextAssessmentPeriod?.id?.toString?.() || assessmentPeriodId?.toString?.() || '';

  const outcomeRows: any[] = useMemo(() => {
    const periodScopedData = isFetchingAreaOutcomes ? currentAreaOutcomesData : areaOutcomesData;
    const result = periodScopedData?.[0]?.result;
    if (Array.isArray(result)) return result;
    if (result?.rows && Array.isArray(result.rows)) return result.rows;
    return [];
  }, [areaOutcomesData, currentAreaOutcomesData, isFetchingAreaOutcomes]);

  const flatOutcomeRows: any[] = useMemo(
    () =>
      outcomeRows.flatMap((item: any) => {
        if (Array.isArray(item.questions)) {
          return item.questions.flatMap((question: any) => {
            if (!Array.isArray(question.aggregates)) {
              return [];
            }

            return question.aggregates.map((aggregate: any) => ({
              ...aggregate,
              outcome_id: item.outcome_id,
              outcome_name: item.outcome_name,
              question_id: question.question_id,
              outcome_question: question.outcome_question,
            }));
          });
        }

        if (item?.aggregates && item?.outcome_question) {
          return [item];
        }

        return [item];
      }),
    [outcomeRows]
  );

  const normalizeOutcomeOptions = (outcomes: any): SelectOption[] => {
    if (!Array.isArray(outcomes)) return [];
    const outcomeMap = new Map<string, SelectOption>();

    outcomes.forEach((outcome: any) => {
      const label = outcome?.name || '';
      const value = outcome?.id?.toString?.() || '';
      if (value && label) {
        outcomeMap.set(value, { value, label });
      }
    });

    return Array.from(outcomeMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  };

  const normalizeCategoryOptions = (categories: any): SelectOption[] => {
    if (!Array.isArray(categories)) return [];
    const categoryMap = new Map<string, SelectOption>();

    categories.forEach((category: any) => {
      const label = category.name || category.label || category.category_name || category.title || '';
      const value = category.id?.toString() || label;
      if (label) {
        categoryMap.set(value, { value, label });
      }
    });

    return Array.from(categoryMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  };

  const outcomeOptions = useMemo(
    () => normalizeOutcomeOptions(selectedOutcomeOptions),
    [selectedOutcomeOptions]
  );

  const categoryOptions = useMemo(() => {
    const normalized = normalizeCategoryOptions(selectedCategories);
    if (normalized.length > 0) {
      return normalized;
    }

    return Array.from(
      new Map(
        flatOutcomeRows
          .filter((item: any) => item.category_id || item.category_name)
          .map((item: any) => [
            item.category_id?.toString() || item.category_name,
            {
              value: item.category_id?.toString() || item.category_name,
              label: item.category_name || item.category_id?.toString(),
            },
          ])
      ).values()
    ).sort((a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label));
  }, [selectedCategories, flatOutcomeRows]);

  const availableOutcomes = outcomeOptions;
  const availableCategories = categoryOptions;
  const hasPeriodOutcomes = availableOutcomes.length > 0;

  useEffect(() => {
    setSelectedCategory(null);
  }, [activeAssessmentPeriodId]);

  // CategorySelect must be given the SAME option set this component defaults
  // against. Passing the raw `selectedCategories` while defaulting against the
  // row-derived fallback made the two disagree: CategorySelect saw an empty
  // `categories` prop, reset the selection to null, and this component's effect
  // immediately re-selected -- an infinite render loop whenever the assessment
  // period has no `categories` structure.
  const categorySelectItems = useMemo(
    () =>
      availableCategories.map((option: SelectOption) => {
        const source = Array.isArray(selectedCategories)
          ? selectedCategories.find((category: any) => category?.id?.toString() === option.value)
          : undefined;
        return { id: option.value, name: option.label, priority: source?.priority };
      }),
    [availableCategories, selectedCategories]
  );

  useEffect(() => {
    if (availableOutcomes.length > 0 && !availableOutcomes.some(option => option.value === selectedOutcome)) {
      setSelectedOutcome(availableOutcomes[0].value);
    } else if (availableOutcomes.length === 0 && selectedOutcome !== null) {
      setSelectedOutcome(null);
    }

    if (availableCategories.length > 0 && !availableCategories.some(option => option.value === selectedCategory)) {
      const primaryCategory = Array.isArray(selectedCategories)
        ? selectedCategories.find((category: any) => category.priority === 'primary')
        : undefined;
      const defaultCategoryId = primaryCategory?.id?.toString() || availableCategories[0]?.value || null;
      setSelectedCategory(defaultCategoryId);
    }
  }, [availableOutcomes, availableCategories, selectedCategories, selectedOutcome, selectedCategory]);

  const promptSelectedOutcomeLabel = availableOutcomes.find(option => option.value === selectedOutcome)?.label || 'the selected outcome';
  const promptSelectedCategoryLabel = availableCategories.find(option => option.value === selectedCategory)?.label || 'the selected category';

  const hasQuestionsSegment = outcomeRows.some((item: any) => Array.isArray(item.questions));
  const legacyOutcomeAggregateFormat = outcomeRows.length > 0 && !hasQuestionsSegment;

  useEffect(() => {
    setPagePrompts([
      {
        title: 'Outcome Question Summary',
        displayText: `Summarize the outcome ${promptSelectedOutcomeLabel} by ${promptSelectedCategoryLabel} and the question-level insights that emerge.`,
        prompt:
          `Review the Outcome Questions page for ${promptSelectedOutcomeLabel} by ${promptSelectedCategoryLabel} and summarize what it shows about question-level performance and demographic patterns. Highlight the strongest signals and how leaders should interpret them.`,
      },
      {
        title: 'Demographic Breakdown Insights',
        displayText: `Surface demographic signals for ${promptSelectedOutcomeLabel} by ${promptSelectedCategoryLabel}.`,
        prompt:
          `Based on the selected outcome ${promptSelectedOutcomeLabel} by ${promptSelectedCategoryLabel}, identify which demographic groups or options show the strongest and weakest question scores. Explain where the biggest gaps are and what those differences might mean for decision-making.`,
      },
      {
        title: 'Question Action Recommendations',
        displayText: `Generate action recommendations for ${promptSelectedOutcomeLabel} by ${promptSelectedCategoryLabel}.`,
        prompt:
          `Recommend practical next steps based on ${promptSelectedOutcomeLabel} by ${promptSelectedCategoryLabel} question scores. Focus on low-scoring questions, areas of divergence by category, and actions to improve alignment with organizational outcomes.`,
      },
    ]);

    setPageInfoTitle('About Outcome Questions');
    setPageInfoIntro(
      'This page shows outcome question scores and their breakdown by demographic category. Use it to understand which questions drive outcome performance and where demographic differences are most pronounced.',
    );
    setPageInfoItems([
      {
        term: 'Question-level signals',
        description: 'See how individual outcome questions are performing within the selected outcome and category.',
      },
      {
        term: 'Demographic comparison',
        description: 'Understand where question scores differ across demographic categories and options.',
      },
      {
        term: 'Action focus',
        description: 'Translate low scores and gaps into recommended actions for leaders and teams.',
      },
    ]);

    return () => {
      setPagePrompts([]);
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    };
  }, [promptSelectedOutcomeLabel, promptSelectedCategoryLabel, setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro]);

  const selectedOutcomeLabel = availableOutcomes.find(option => option.value === selectedOutcome)?.label || '';
  const selectedCategoryLabel = availableCategories.find(option => option.value === selectedCategory)?.label || '';
  const chartTitle = selectedOutcomeLabel && selectedCategoryLabel ? `${selectedOutcomeLabel} by ${selectedCategoryLabel}` : 'Outcome Area Scores';

  const chartRows: any[] = useMemo(() => {
    if (!selectedOutcome || !hasPeriodOutcomes) {
      return [];
    }

    const selectedOutcomeStillAvailable = availableOutcomes.some(option => option.value === selectedOutcome);
    if (!selectedOutcomeStillAvailable) {
      return [];
    }

    const matched = flatOutcomeRows.filter((item: any) => {
      const outcomeMatches =
        item.outcome_id?.toString() === selectedOutcome ||
        item.outcome_name === selectedOutcome;
      const categoryMatches =
        item.category_id?.toString() === selectedCategory ||
        item.category_name === selectedCategory;

      return outcomeMatches && categoryMatches;
    });

    const grouped = new Map<string, any>();

    matched.forEach((item: any) => {
      const key = item.option_id?.toString() || item.option_name || JSON.stringify(item);
      if (!grouped.has(key)) {
        grouped.set(key, {
          ...item,
          totalScore: (item.overall_score || 0) * (item.n || 0),
          totalN: item.n || 0,
          displayN: item.n || 0,
        });
      } else {
        const existing = grouped.get(key);
        existing.totalScore += (item.overall_score || 0) * (item.n || 0);
        existing.totalN += item.n || 0;
        existing.displayN = Math.max(existing.displayN || 0, item.n || 0);
        existing.overall_score = existing.totalN ? existing.totalScore / existing.totalN : existing.overall_score;
      }
    });

    return Array.from(grouped.values()).map((item: any) => ({
      ...item,
      overall_score: Number(item.overall_score),
      n: item.displayN ?? item.n,
    }));
  }, [flatOutcomeRows, selectedOutcome, selectedCategory, hasPeriodOutcomes, availableOutcomes]);

  const chartLabels = chartRows.map((item: any) => item.option_name);
  const chartScores = chartRows.map((item: any) => Number((item.overall_score ?? 0).toFixed(1)));
  const chartColors = chartRows.map((item: any) => item.color || '#4a90e2');
  const chartBorderColors = chartRows.map((item: any) => hexToRgb(item.color || '#4a90e2', 0.9));
  const respondentCounts = chartRows.map((item: any) => item.n);

  const isOutcomeDataLoading = isLoadingAreaOutcomes || isFetchingAreaOutcomes;
  const shouldShowNoDataCard =
    !isOutcomeDataLoading &&
    (!hasPeriodOutcomes || (!legacyOutcomeAggregateFormat && chartRows.length === 0));

  return (
    <PageContent>
      <PageSection padbottom="10px">
        <PageHeader
          title="Outcome Questions"
          caption="Outcomes scores and question breakdown by demographic"
        />
      </PageSection>
      <PageSection>
        {shouldShowNoDataCard ? (
          <DashboardCard>
            <PageSection display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 300, width: '100%' }}>
              <div>No outcomes data is available for the selected assessment period.</div>
            </PageSection>
          </DashboardCard>
        ) : (
          <DashboardCard
            title={`${chartTitle} (${contextAssessmentPeriod?.name})`}
            controls={
              <>
                <Select
                  options={availableOutcomes}
                  placeholder="Select Outcome"
                  value={availableOutcomes.find(option => option.value === selectedOutcome)}
                  onChange={option => setSelectedOutcome(option ? option.value : null)}
                  styles={{
                    container: base => ({
                      ...base,
                      width: '220px',
                    }),
                  }}
                />
                <CategorySelect
                  categories={categorySelectItems}
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                  placeholder="Select Category"
                  width="220px"
                />
              </>
            }
          >
            <PageSection style={{ minHeight: 420, position: 'relative', width: '100%' }}>
              {isOutcomeDataLoading ? (
                <PageSection display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 300, width: '100%' }}>
                  <LoadingSpinner />
                </PageSection>
              ) : legacyOutcomeAggregateFormat ? (
                <PageSection display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 300, width: '100%' }}>
                  <div>Outcome aggregate information is not available for this assessment period.</div>
                </PageSection>
              ) : (
                <Bar
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          label: (tooltipItem: any) => {
                            const index = tooltipItem.dataIndex;
                            const value = tooltipItem.formattedValue;
                            const n = respondentCounts[index];
                            return `Score: ${value} (${n} respondents)`;
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        min: 0,
                        max: 10,
                        ticks: {
                          stepSize: 1,
                        },
                      },
                    },
                  }}
                  data={{
                    labels: chartLabels,
                    datasets: [
                      {
                        label: 'Culture Score',
                        data: chartScores,
                        backgroundColor: chartColors.map((color: string) => hexToRgb(color, 0.6)),
                        borderColor: chartBorderColors,
                        borderWidth: 2,
                      },
                    ],
                  }}
                  style={{ width: '100%', height: '350px' }}
                />
              )}
            </PageSection>
          </DashboardCard>
        )}
      </PageSection>

      {!legacyOutcomeAggregateFormat && !shouldShowNoDataCard && (
        <PageSection padtop>
          <OutcomesAreaQuestions
            outcomeRows={outcomeRows}
            selectedOutcome={selectedOutcome}
            selectedCategory={selectedCategory}
              isLoading={isOutcomeDataLoading}
          />
        </PageSection>
      )}
    </PageContent>
  );
};

interface OutcomeQuestionDetail {
  option_name: string;
  overall_score: number;
  n: number;
  color: string;
}

interface OutcomeQuestionRow {
  question: string;
  overall_score: number;
  confidence_interval: {
    start: number;
    end: number;
  };
  hasQuestionLevelSummary?: boolean;
  details: OutcomeQuestionDetail[];
}

const OutcomesAreaQuestions = ({
  outcomeRows,
  selectedOutcome,
  selectedCategory,
  isLoading,
}: {
  outcomeRows: any[];
  selectedOutcome: string | null;
  selectedCategory: string | null;
  isLoading: boolean;
}) => {
  const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});

  const toggleRow = (index: number) => {
    setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleAllRows = () => {
    const allExpanded = questionRows.length > 0 && questionRows.every((_, index) => expandedRows[index]);
    const nextValue = !allExpanded;
    const nextExpandedRows = questionRows.reduce((acc, _, index) => ({ ...acc, [index]: nextValue }), {} as { [key: number]: boolean });
    setExpandedRows(nextExpandedRows);
  };

  const questionRows = useMemo(() => {
    const questionEntries = outcomeRows.flatMap((item: any) => {
      if (!item || !Array.isArray(item.questions)) {
        if (item?.outcome_question && Array.isArray(item?.aggregates)) {
          return [
            {
              ...item,
              question_id: item.question_id || item.q,
            },
          ];
        }
        return [];
      }

      return item.questions.map((question: any) => ({
        ...question,
        outcome_id: item.outcome_id,
        outcome_name: item.outcome_name,
      }));
    });

    const filteredRows = questionEntries.filter((item: any) => {
      const outcomeMatches =
        item.outcome_id?.toString() === selectedOutcome ||
        item.outcome_name === selectedOutcome;
      const categoryMatches = selectedCategory
        ? (item.aggregates || []).some(
            (aggregate: any) =>
              aggregate.category_id?.toString() === selectedCategory ||
              aggregate.category_name === selectedCategory
          )
        : true;

      return outcomeMatches && categoryMatches;
    });

    const grouped = new Map<string, OutcomeQuestionRow>();

    filteredRows.forEach((item: any) => {
      const questionLabel = item.outcome_question || item.question_text || 'Question';
      const key = `${item.outcome_id || 'unknown'}::${item.question_id || questionLabel}`;
      const filteredAggregates = (item.aggregates || []).filter((aggregate: any) => {
        if (!selectedCategory) return true;
        return (
          aggregate.category_id?.toString() === selectedCategory ||
          aggregate.category_name === selectedCategory
        );
      });
      const interval = filteredAggregates.reduce(
        (acc: { start: number; end: number }, aggregate: any) => ({
          start: Math.min(acc.start, aggregate.confidence_interval?.[0] ?? 0),
          end: Math.max(acc.end, aggregate.confidence_interval?.[1] ?? 0),
        }),
        { start: Number.POSITIVE_INFINITY, end: Number.NEGATIVE_INFINITY }
      );

      const questionOverallScore = typeof item.overall_score === 'number' ? item.overall_score : undefined;
      const questionConfidenceInterval = item.confidence_interval;
      const questionInterval = questionConfidenceInterval
        ? {
            start: questionConfidenceInterval.start ?? 0,
            end: questionConfidenceInterval.end ?? 0,
          }
        : interval;

      if (!grouped.has(key)) {
        grouped.set(key, {
          question: questionLabel,
          overall_score: questionOverallScore ?? 0,
          confidence_interval: {
            start: questionInterval.start === Number.POSITIVE_INFINITY ? 0 : questionInterval.start,
            end: questionInterval.end === Number.NEGATIVE_INFINITY ? 0 : questionInterval.end,
          },
          hasQuestionLevelSummary: questionOverallScore !== undefined || !!questionConfidenceInterval,
          details: [],
        });
      }

      const row = grouped.get(key)!;
      row.confidence_interval.start = Math.min(
        row.confidence_interval.start,
        questionInterval.start === Number.POSITIVE_INFINITY ? 0 : questionInterval.start
      );
      row.confidence_interval.end = Math.max(
        row.confidence_interval.end,
        questionInterval.end === Number.NEGATIVE_INFINITY ? 0 : questionInterval.end
      );

      if (typeof questionOverallScore === 'number') {
        row.overall_score = questionOverallScore;
      }

      filteredAggregates.forEach((aggregate: any) => {
        row.details.push({
          option_name: aggregate.option_name,
          overall_score: Number(aggregate.overall_score),
          n: Number(aggregate.n) || 0,
          color: aggregate.color || '#4a90e2',
        });
      });
    });

    const rows = Array.from(grouped.values());
    rows.forEach(row => {
      const detailMap = new Map<string, OutcomeQuestionDetail>();
      row.details.forEach(detail => {
        const key = detail.option_name || `${detail.overall_score}-${detail.n}`;
        if (!detailMap.has(key)) {
          detailMap.set(key, detail);
        }
      });
      row.details = Array.from(detailMap.values());

      if (!row.hasQuestionLevelSummary) {
        const totalRespondents = row.details.reduce((sum, detail) => sum + detail.n, 0);
        row.overall_score = totalRespondents
          ? row.details.reduce((sum, detail) => sum + detail.overall_score * detail.n, 0) / totalRespondents
          : row.details.reduce((sum, detail) => sum + detail.overall_score, 0) / Math.max(row.details.length, 1);
      }
    });

    return rows;
  }, [outcomeRows, selectedOutcome, selectedCategory]);

  return (
    <DashboardCard
      title="Questions Details"
      controls={
        <StyledIconButton
          icon={questionRows.length > 0 && questionRows.every((_, index) => expandedRows[index]) ? 'minus' : 'plus'}
          title={questionRows.length > 0 && questionRows.every((_, index) => expandedRows[index]) ? 'Collapse all question rows' : 'Expand all question rows'}
          onClick={toggleAllRows}
          color="grey"
        />
      }
    >
      <PageSection
        padtop="30px"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        style={{ width: '100%', minHeight: '80%' }}
      >
        {isLoading ? (
          <PageSection display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 250, width: '100%' }}>
            <LoadingSpinner />
          </PageSection>
        ) : questionRows.length === 0 ? (
          <PageSection display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 250, width: '100%' }}>
            <div>No question-level outcome data is available for the selected filters.</div>
          </PageSection>
        ) : (
          questionRows.map((row, index) => (
            <div key={index}>
              <Row onClick={() => toggleRow(index)}>
                <QuestionColumn>{row.question}</QuestionColumn>
                <ScoreColumn>
                  <strong>{row.overall_score.toFixed(1)}</strong>
                </ScoreColumn>
                <BarColumn>
                  {index === 0 && (
                    <RowLegend>
                      {Array.from(Array(11).keys()).map(num => (
                        <span key={num}>{num}</span>
                      ))}
                    </RowLegend>
                  )}
                  <ProgressBar>
                    {(() => {
                      const rawStart = (row.confidence_interval.start / 10) * 100;
                      const rawWidth = ((row.confidence_interval.end - row.confidence_interval.start) / 10) * 100;
                      const start = Math.max(0, Math.min(100, rawStart));
                      const width = Math.max(0, Math.min(100 - start, rawWidth));
                      const markerPos = Math.max(0, Math.min(100, (row.overall_score / 10) * 100));

                      return (
                        <>
                          <ConfidenceBar start={start} width={width} />
                          <ScoreMarker position={markerPos} />
                        </>
                      );
                    })()}
                  </ProgressBar>
                </BarColumn>
              </Row>
              <SubTable className={expandedRows[index] ? 'expanded' : ''}>
                {row.details.map((detail, detailIndex) => (
                  <Row key={detailIndex}>
                    <OptionColumn>{detail.option_name}</OptionColumn>
                    <BarColumn>
                      <DepartmentBar style={{ width: `${(detail.overall_score / 10) * 100}%` }}>
                        <DepartmentLabel>{detail.overall_score.toFixed(1)}</DepartmentLabel>
                      </DepartmentBar>
                    </BarColumn>
                  </Row>
                ))}
              </SubTable>
            </div>
          ))
        )}
      </PageSection>
    </DashboardCard>
  );
};

const Row = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #ccc;
  padding: 10px 0;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #f9f9f9;
  }
`;

const QuestionColumn = styled.div`
  flex: 4;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding-right: 20px;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  max-width: 800px;
  color: #555;
`;

const ScoreColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding-right: 20px;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  max-width: 800px;
  color: #555;
`;

const OptionColumn = styled.div`
  flex: 5;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-right: 35px;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  max-width: 800px;
  color: #777;
`;

const BarColumn = styled.div`
  flex: 8;
  position: relative;
`;

const SubTable = styled.div`
  margin-top: 10px;
  max-height: 0;
  overflow: hidden;
  transition: max-height 1s ease;

  &.expanded {
    max-height: 1000px;
  }
`;

const ProgressBar = styled.div`
  background: #e0e0e0;
  height: 20px;
  position: relative;
  margin: 5px 0;
  border-radius: 5px;
`;

const ConfidenceBar = styled.div<{ start: number; width: number }>`
  position: absolute;
  background: #f88;
  height: 20px;
  left: ${props => props.start}%;
  width: ${props => props.width}%;
  border-radius: 5px;
  transition:
    left 0.5s ease,
    width 0.5s ease;
`;

const ScoreMarker = styled.div<{ position: number }>`
  position: absolute;
  background: #fff;
  border: 1px solid #777;
  border-radius: 50%;
  width: 15px;
  height: 15px;
  top: 50%;
  left: ${props => props.position}%;
  transform: translate(-50%, -50%);
  transition: left 0.5s ease;
`;

const DepartmentBar = styled.div`
  background: #a5cfee;
  height: 20px;
  position: relative;
  margin: 5px 0;
  border-radius: 5px;
  transition:
    left 0.5s ease,
    width 0.5s ease;
`;

const DepartmentLabel = styled.div`
  position: absolute;
  left: calc(100% + 10px);
  font-size: 12px;
  color: #333;
  white-space: nowrap;
`;

const RowLegend = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-top: -20px;
  margin-bottom: 10px;
  color: #666;
  width: 100%;
  position: relative;
`;

export default OutcomesQuestions;


